import { useEffect, useRef } from 'react';

import {
  getPaypalExpressSuccessRedirectFromQuery,
  hasPaypalExpressSuccessRedirectReturnSearch,
  type PaypalExpressQuery,
  seedPaypalExpressRedirectSession,
} from '@/components/PaypalExpressButton/helpers';
import { paypalExpressRedirectRecoveryRef } from '@/hooks/paypalExpressRedirectRef';
import { useRouter } from '@/hooks/useRouter';
import { paths } from '@/lib/paths';
import { PAYPAL_EXPRESS_QUERY_PARAMS } from '@/lib/payment/queryParams';
import { StorageHelper } from '@/lib/storage';
import { checkoutActions } from '@/states/checkout';

// PayPal Express cannot be split, so a return to pay-by-link is always another method.
const PAY_BY_LINK_PATH = '/store/order/payment/';

const isPayByLinkPath = (path: string): boolean =>
  path.includes(PAY_BY_LINK_PATH);

export const usePaypalExpressSuccessRedirect = () => {
  const { push, query } = useRouter();
  const storageHelperRef = useRef(new StorageHelper());

  const completeSuccessRedirectRef = useRef(
    (searchOrQuery: { query?: PaypalExpressQuery; search?: string }) => {
      if (
        paypalExpressRedirectRecoveryRef.current ||
        isPayByLinkPath(window.location.pathname)
      ) {
        return;
      }

      const queryFromSearch =
        searchOrQuery.search !== undefined
          ? (() => {
              const params = new URLSearchParams(searchOrQuery.search);
              const parsed: PaypalExpressQuery = {};
              params.forEach((value, key) => {
                parsed[key] = value;
              });
              return parsed;
            })()
          : searchOrQuery.query;

      if (!queryFromSearch) return;

      const payload = getPaypalExpressSuccessRedirectFromQuery(queryFromSearch);
      if (!payload) return;

      paypalExpressRedirectRecoveryRef.current = true;

      seedPaypalExpressRedirectSession(storageHelperRef.current, payload);
      checkoutActions.setOrderId(payload.orderId);

      const reviewExpressUrl = paths.store.checkout.reviewExpress.url({
        query: {
          [PAYPAL_EXPRESS_QUERY_PARAMS.orderId]: payload.orderId,
        },
      });

      void push(reviewExpressUrl);
    },
  );

  useEffect(() => {
    let isInitialPageShow = true;

    const onPageShow = () => {
      const search = window.location.search;

      if (
        isInitialPageShow &&
        hasPaypalExpressSuccessRedirectReturnSearch(search)
      ) {
        completeSuccessRedirectRef.current({ search });
      }

      isInitialPageShow = false;
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    completeSuccessRedirectRef.current({ query });
  }, [query]);
};
